import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { qtyDisplay } from "@icraftx/shared/v1";
import type { DeskGlance } from "@/shared/api/desk";
import {
  chipDisplayValue,
  formatGlanceMoney,
  ownerGlanceChips,
  sparklinePoints,
  vsYesterdayLabel,
} from "./owner-glance-math";

/** Collapsed + expandable owner glance — plan/44 · plan/45 Show more */
export function OwnerGlanceBand(props: {
  companySlug: string;
  currencyCode: string;
  glance: DeskGlance | null;
  loading?: boolean;
  /** When true, show Trial balance deep-link in expand — plan/44 */
  canSeeGl?: boolean;
  className?: string;
}) {
  const { companySlug, currencyCode, glance, loading, canSeeGl, className } =
    props;
  const [expanded, setExpanded] = useState(false);

  if (loading && !glance) {
    return (
      <p className={cn("text-sm text-[var(--color-muted)]", className)}>
        Loading glance…
      </p>
    );
  }
  if (!glance) return null;

  const chips = ownerGlanceChips(companySlug, glance);
  if (chips.length === 0) return null;

  return (
    <section className={cn(className)} aria-label="Owner glance">
      {/* band wrap <lg · stacked rail ≥lg (plan/44 placement) */}
      <div className="flex flex-wrap gap-2 lg:flex-col lg:flex-nowrap">
        {chips.map((chip) => (
          <Link
            key={chip.key}
            to={chip.to}
            className={cn(
              "inline-flex rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/90 no-underline shadow-[var(--shadow-soft)] transition-colors hover:border-[var(--color-primary)]/40",
              "min-w-[7.5rem] flex-col px-3 py-2.5",
              "lg:w-full lg:min-w-0 lg:flex-row lg:items-baseline lg:justify-between lg:gap-3",
            )}
          >
            <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              {chip.label}
            </span>
            <span className="mt-0.5 font-[family-name:var(--font-display)] text-lg font-semibold tabular-nums tracking-tight text-[var(--color-foreground)] lg:mt-0 lg:text-base">
              {chipDisplayValue(chip, currencyCode)}
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-2">
        <button
          type="button"
          className="text-xs font-medium text-[var(--color-primary)] hover:underline"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      </div>

      {expanded ? (
        <div className="mt-3 rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/90 px-3 py-3 shadow-[var(--shadow-soft)]">
          <p className="m-0 text-xs text-[var(--color-muted)]">
            Sales today = POS + delivered SO − returns (tax incl). Low stock =
            active stocked items with on-hand ≤{" "}
            {qtyDisplay(glance.lowStock?.threshold ?? "0")}. Queue rows open the
            matching list.
          </p>
          <ul className="m-0 mt-3 list-none space-y-2 p-0">
            {chips.map((chip) => {
              const detail =
                chip.amount != null
                  ? (formatGlanceMoney(chip.amount, currencyCode, {
                      maxFractionDigits: 2,
                      zeroOk: true,
                    }) ?? chip.amount)
                  : (() => {
                      const money = formatGlanceMoney(
                        chip.outstanding,
                        currencyCode,
                        { maxFractionDigits: 2 },
                      );
                      return `${chip.count ?? 0}${money != null ? ` · ${money}` : ""}`;
                    })();
              const vs =
                chip.amount != null && chip.yesterdayAmount != null
                  ? vsYesterdayLabel(
                      chip.amount,
                      chip.yesterdayAmount,
                      currencyCode,
                    )
                  : null;
              return (
                <li key={`exp-${chip.key}`}>
                  <Link
                    to={chip.to}
                    className="flex flex-col gap-0.5 text-sm no-underline hover:underline"
                  >
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-[var(--color-foreground)]">
                        {chip.label}
                      </span>
                      <span className="tabular-nums text-[var(--color-muted)]">
                        {detail}
                      </span>
                    </span>
                    {vs ? (
                      <span className="text-xs text-[var(--color-muted)]">
                        {vs}
                      </span>
                    ) : null}
                    {chip.last7 && chip.last7.length === 7 ? (
                      <span className="mt-1 block" aria-hidden>
                        <svg
                          viewBox="0 0 84 28"
                          width={84}
                          height={28}
                          className="overflow-visible"
                        >
                          <polyline
                            fill="none"
                            stroke="var(--color-primary)"
                            strokeWidth="1.5"
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            points={sparklinePoints(chip.last7)}
                          />
                        </svg>
                        <span className="sr-only">Sales last 7 days</span>
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
          {canSeeGl ? (
            <p className="mb-0 mt-3 border-t border-[var(--color-border)]/80 pt-3 text-xs">
              <Link
                to={`/c/${companySlug}/gl/trial-balance`}
                className="font-medium text-[var(--color-primary)] no-underline hover:underline"
              >
                Trial balance
              </Link>
              <span className="text-[var(--color-muted)]"> · books</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
